import { validateIBAN, ValidationErrorsIBAN, validateBIC, ValidationErrorsBIC } from 'ibantools'
import z from 'zod'
import { countrySchema } from './enums/countries.schema'

const ibanErrorMessages: Record<ValidationErrorsIBAN, string> = {
  [ValidationErrorsIBAN.NoIBANProvided]: 'Keine IBAN angegeben',
  [ValidationErrorsIBAN.NoIBANCountry]: 'Unbekanntes IBAN-Land',
  [ValidationErrorsIBAN.WrongBBANLength]: 'Falsche BBAN-Länge',
  [ValidationErrorsIBAN.WrongBBANFormat]: 'Falsches BBAN-Format',
  [ValidationErrorsIBAN.ChecksumNotNumber]: 'Prüfsumme ist keine Zahl',
  [ValidationErrorsIBAN.WrongIBANChecksum]: 'Falsche IBAN-Prüfsumme',
  [ValidationErrorsIBAN.WrongAccountBankBranchChecksum]: 'Falsche Konto-/Bankprüfsumme',
  [ValidationErrorsIBAN.QRIBANNotAllowed]: 'QR-IBAN nicht erlaubt',
}

const bicErrorMessages: Record<ValidationErrorsBIC, string> = {
  [ValidationErrorsBIC.NoBICProvided]: 'Keine BIC angegeben',
  [ValidationErrorsBIC.NoBICCountry]: 'Unbekanntes BIC-Land',
  [ValidationErrorsBIC.WrongBICFormat]: 'Falsches BIC-Format',
}

export const vatIdOrTaxNumberRequiredMessage = 'USt-IdNr und/oder Steuernummer – mindestens eine Angabe erforderlich'

export const BatteryCounterpartySchema = z
  .object({
    counterpartyName: z.string().trim().min(1).describe('Name des Vertragspartners'),
    addressCountry: countrySchema.describe('Land'),
    addressZipcode: z.string().length(5).describe('PLZ'),
    addressCity: z.string().trim().min(1).describe('Stadt'),
    addressStreet: z.string().trim().min(1).describe('Straße'),
    addressStreetNumber: z.string().trim().min(1).describe('Hausnummer'),
    tradeRegisterNumber: z.string().describe('Handelsregisternummer'),
    bankAccountBank: z.string().describe('Bank'),
    bankAccountOwner: z.string().describe('Kontoinhaber'),
    bankAccountIban: z
      .string()
      .transform((val) => val.replace(/\s/g, ''))
      .superRefine((val, ctx) => {
        const result = validateIBAN(val)
        if (!result.valid) {
          for (const code of result.errorCodes) {
            ctx.addIssue({
              code: 'custom',
              message: ibanErrorMessages[code],
            })
          }
        }
      })
      .describe('IBAN'),
    bankAccountBic: z
      .string()
      .superRefine((val, ctx) => {
        const result = validateBIC(val)
        if (!result.valid) {
          for (const code of result.errorCodes) {
            ctx.addIssue({
              code: 'custom',
              message: bicErrorMessages[code],
            })
          }
        }
      })
      .describe('BIC'),
    vatId: z.string().optional().describe('USt-IdNr.').meta({
      updateOn: 'change',
    }),
    taxNumber: z.string().optional().describe('Steuernummer').meta({
      updateOn: 'change',
    }),
    remitRelevance: z.boolean().default(false).describe('REMIT-Relevanz'),
  })
  .describe('Vertragspartner')
  .check((ctx) => {
    const { vatId, taxNumber } = ctx.value
    if (!vatId && !taxNumber) {
      ;['vatId', 'taxNumber'].forEach((field) => {
        ctx.issues.push({
          code: 'custom',
          input: ctx.value,
          path: [field],
          message: vatIdOrTaxNumberRequiredMessage,
        })
      })
    }
  })

export const BatteryCounterpartySchemaForSharepoint = BatteryCounterpartySchema.safeExtend({
  counterpartyValidFrom: z.coerce.date(),
  counterpartyValidUntil: z.coerce.date(),
})
